import { useState } from 'react';

import { AppIcon } from '@backrs_ui/common-ui/dist';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  PRIORITY_TASK_STATUS_CODES,
  PriorityTaskStatusType,
  TaskStatusCode,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.types';

import { Box, Button, Chip, Popover, Radio, SxProps, Theme, Typography } from '@mui/material';

const TASK_STATUS_CHIP_STRINGS = appStrings.modules.priorities.taskCard.statusPopoverFooter;

type TaskStatusChipProps = Readonly<{
  statusCode: TaskStatusCode | null;
  allStatuses?: PriorityTaskStatusType[];
  updateTaskStatus?: (statusCode: TaskStatusCode) => void;
  disabled?: boolean;
}>;

export const TaskStatusChip = ({
  statusCode,
  allStatuses,
  updateTaskStatus,
  disabled,
}: TaskStatusChipProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const notStartedStatus: PriorityTaskStatusType | null =
    allStatuses?.find((s) => s.code === PRIORITY_TASK_STATUS_CODES.NOT_STARTED) || null;

  const currentStatus = allStatuses?.find((s) => s.code === statusCode) || notStartedStatus;

  const [localSelectedStatus, setLocalSelectedStatus] = useState<PriorityTaskStatusType | null>(
    null,
  );

  if (!allStatuses || !allStatuses.length) {
    return null;
  }

  const displayedStatus = localSelectedStatus || currentStatus;

  const getVisualChip = (code: PriorityTaskStatusType['code'], showIcon = true) => {
    const statusName = allStatuses?.find((s) => s.code === code)?.display_name;

    switch (code) {
      case PRIORITY_TASK_STATUS_CODES.NOT_STARTED:
        return <Chip label={statusName} variant="pill" size="sm" sx={notStartedChipSx} />;

      case PRIORITY_TASK_STATUS_CODES.IN_PROGRESS:
        return <Chip label={statusName} variant="pill" size="sm" sx={inProgressChipSx} />;

      case PRIORITY_TASK_STATUS_CODES.COMPLETED:
        return (
          <Chip
            label={statusName}
            variant="pill"
            size="sm"
            sx={completedChipSx(showIcon)}
            {...(showIcon && {
              icon: <AppIcon iconName="Check" diameter="12px" color="Success/700" />,
            })}
          />
        );
    }
  };

  const statusPopover = () => {
    return (
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        TransitionProps={{
          onExited: () => {
            // once the Popover is closed, then we can reset the state.
            // onExited is used to prevent the UI from setting it back to the correct status while the close animation is running
            setLocalSelectedStatus(null);
          },
        }}
        PaperProps={{
          sx: popoverContainerSx,
        }}
      >
        <Box sx={popoverContentSx}>
          {allStatuses.map((item) => (
            <Box sx={popoverItemSx} key={item.id} onClick={() => setLocalSelectedStatus(item)}>
              <Box sx={popoverItemRightIconSx}>
                <Radio checked={item.id === displayedStatus?.id} />
              </Box>

              {getVisualChip(item.code, false)}
            </Box>
          ))}
        </Box>

        <Box sx={popoverFooterSx}>
          <Button
            id="task-status-update-button"
            variant="text"
            size="sm"
            sx={popoverFooterButtonSx}
            onClick={() => {
              if (localSelectedStatus?.code !== statusCode && localSelectedStatus?.code) {
                updateTaskStatus?.(localSelectedStatus.code);
              }

              setAnchorEl(null);
            }}
          >
            <Typography
              variant="text-sm-semibold"
              color={({ backrs }) => backrs.colors['Colors/Text/text-brand-secondary']}
            >
              {TASK_STATUS_CHIP_STRINGS}
            </Typography>
          </Button>
        </Box>
      </Popover>
    );
  };

  return (
    <Box>
      <Box
        onClick={(e) => {
          if (disabled) return;

          setAnchorEl(e.currentTarget);
          setLocalSelectedStatus(currentStatus);
        }}
        sx={statusChipContainerSx(!disabled)}
      >
        {currentStatus?.code && getVisualChip(currentStatus?.code)}
      </Box>

      {statusPopover()}
    </Box>
  );
};

const notStartedChipSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/700'],
  border: `1.4px solid ${backrs.colors['Gray/600']}`,
  paddingX: backrs.spacing['spacing-md'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

const inProgressChipSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-brand-secondary'],
  border: `1.5px solid ${backrs.colors['Colors/Border/border-brand-alt']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  paddingX: backrs.spacing['spacing-md'],
});

const completedChipSx =
  (showIcon: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    color: backrs.colors['Success/700'],
    border: `1.5px solid ${backrs.colors['Success/600']}`,
    gap: backrs.spacing['spacing-xs'],
    paddingLeft: backrs.spacing[showIcon ? 'spacing-sm' : 'spacing-md'],
    paddingRight: backrs.spacing['spacing-md'],
    backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  });

const popoverContainerSx: SxProps<Theme> = ({ backrs }) => ({
  boxShadow: backrs.shadows['shadow-md'],
  borderRadius: backrs.radius['radius-md'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
});

const popoverContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  paddingBottom: backrs.spacing['spacing-lg'],
  paddingTop: backrs.spacing['spacing-md'],
});

const popoverItemSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  paddingTop: backrs.spacing['spacing-md'],

  paddingRight: backrs.spacing['spacing-4xl'],
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  cursor: 'pointer',
  userSelect: 'none',
});

const popoverItemRightIconSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'center',
  paddingLeft: backrs.spacing['spacing-xl'],
  '& .MuiRadio-root': {
    margin: backrs.spacing['spacing-none'],
  },
});

const popoverFooterSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  paddingY: backrs.spacing['spacing-lg'],
  paddingX: backrs.spacing['spacing-xl'],
  borderTop: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
});

const popoverFooterButtonSx: SxProps<Theme> = ({ backrs }) => ({
  padding: backrs.spacing['spacing-none'],
  justifyContent: 'flex-end',
  textTransform: 'none',

  '&:hover': {
    backgroundColor: `${backrs.colors['Colors/Background/bg-primary']} !important`,
  },
});

const statusChipContainerSx = (isClickable?: boolean): SxProps<Theme> => ({
  transition: 'background-color 0.3s ease-in-out',
  ...(isClickable && {
    cursor: 'pointer',
  }),
});
